// NOAA G-Scale inspired palette — approximate hex values from the SWPC reference table.
export const G_SCALE_COLORS = {
  1: '#FFE800', // G1 Minor — vivid yellow
  2: '#FFCC33', // G2 Moderate — golden amber
  3: '#FF8C00', // G3 Strong — deep orange
  4: '#FF3333', // G4 Severe — bright red
  5: '#990000', // G5 Extreme — dark red
};

// Dst thresholds (nT) at the start of each storm level, with associated value bands.
export const DST_G_SCALE_LEVELS = [
  { level: 1, label: 'G1', value: -30, minBound: -50, maxBound: -30, color: G_SCALE_COLORS[1] },
  { level: 2, label: 'G2', value: -50, minBound: -100, maxBound: -50, color: G_SCALE_COLORS[2] },
  { level: 3, label: 'G3', value: -100, minBound: -200, maxBound: -100, color: G_SCALE_COLORS[3] },
  { level: 4, label: 'G4', value: -200, minBound: -350, maxBound: -200, color: G_SCALE_COLORS[4] },
  { level: 5, label: 'G5', value: -350, minBound: -Infinity, maxBound: -350, color: G_SCALE_COLORS[5] },
];

const MIN_GUIDE_SPACING_PX = 14;

export const isInGScaleBand = (value, guide) => {
  if (value === null || value === undefined || Number.isNaN(value)) return false;
  if (guide.level === 5) return value <= guide.maxBound;
  return value <= guide.maxBound && value > guide.minBound;
};

export const computeGuideIntervals = (data, valueAccessors, guide) => {
  if (!data.length || !valueAccessors.length) return [];

  const inBandAt = (d) =>
    valueAccessors.some((accessor) => isInGScaleBand(accessor(d), guide));

  const intervals = [];
  let startIdx = null;

  for (let i = 0; i < data.length; i += 1) {
    if (inBandAt(data[i])) {
      if (startIdx === null) startIdx = i;
    } else if (startIdx !== null) {
      intervals.push({ startIdx, endIdx: i - 1 });
      startIdx = null;
    }
  }

  if (startIdx !== null) {
    intervals.push({ startIdx, endIdx: data.length - 1 });
  }

  return intervals;
};

export const intervalHalfMs = (data, idx, direction) => {
  if (data.length < 2) return 30 * 60 * 1000;

  if (direction < 0) {
    if (idx <= 0) {
      const t0 = new Date(data[0].date).getTime();
      const t1 = new Date(data[1].date).getTime();
      return Math.abs(t1 - t0) / 2;
    }
    const t0 = new Date(data[idx - 1].date).getTime();
    const t1 = new Date(data[idx].date).getTime();
    return Math.abs(t1 - t0) / 2;
  }

  if (idx >= data.length - 1) {
    const t0 = new Date(data[data.length - 2].date).getTime();
    const t1 = new Date(data[data.length - 1].date).getTime();
    return Math.abs(t1 - t0) / 2;
  }
  const t0 = new Date(data[idx].date).getTime();
  const t1 = new Date(data[idx + 1].date).getTime();
  return Math.abs(t1 - t0) / 2;
};

export const intervalToXRange = (data, zx, interval) => {
  const startT = new Date(data[interval.startIdx].date).getTime();
  const endT = new Date(data[interval.endIdx].date).getTime();
  const x1 = zx(new Date(startT - intervalHalfMs(data, interval.startIdx, -1)));
  const x2 = zx(new Date(endT + intervalHalfMs(data, interval.endIdx, 1)));
  return { x1: Math.min(x1, x2), x2: Math.max(x1, x2) };
};

export const selectDstGuideLevels = (levels, yScale, innerHeight) => {
  const inRange = levels.filter((g) => {
    const py = yScale(g.value);
    return py >= 0 && py <= innerHeight;
  });

  const hasRoom = (guides) => {
    const positions = guides.map((g) => yScale(g.value)).sort((a, b) => a - b);
    for (let i = 1; i < positions.length; i += 1) {
      if (positions[i] - positions[i - 1] < MIN_GUIDE_SPACING_PX) return false;
    }
    return true;
  };

  if (hasRoom(inRange)) return inRange;
  return inRange.filter((g) => g.level === 1 || g.level === 3 || g.level === 4 || g.level === 5);
};

export const guidesWithActiveIntervals = (data, valueAccessors, guides) =>
  guides.filter((guide) => computeGuideIntervals(data, valueAccessors, guide).length > 0);
